import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { supabase } from '../lib/supabase';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Debug: Log URL parameters and hash
        console.log('Auth callback URL parameters:', Object.fromEntries(searchParams.entries()));
        console.log('Auth callback hash:', window.location.hash);
        
        // If there are error parameters in the URL, display them
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (errorParam) {
          console.error('Auth callback error:', errorParam, errorDescription);
          setError(errorDescription || 'Er is een fout opgetreden bij het verifiëren van uw account.');
          // Still navigate to login after a short delay
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Check if this is an invite link (from admin-created users)
        // Look for type=invite in query params or hash fragment
        const typeParam = searchParams.get('type');
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const typeInHash = hashParams.get('type');
        
        // Debug all query parameters
        console.log('Query params:', Object.fromEntries(searchParams.entries()));
        console.log('Hash params:', Object.fromEntries(hashParams.entries()));
        console.log('URL:', window.location.href);
        
        // Check if link contains invite token or if URL contains key invite words
        const urlContainsInviteWords = 
          window.location.href.includes('invite') || 
          window.location.href.includes('verification') || 
          window.location.href.includes('verify');
        
        const isInviteLink = 
          typeParam === 'invite' || 
          typeInHash === 'invite' || 
          urlContainsInviteWords;
        
        console.log('Is this an invite link?', isInviteLink);
        
        // If this is an invite link, sign out any existing session first
        // This prevents changing the wrong account's password
        if (isInviteLink) {
          console.log('Invite link detected, signing out any existing session first');
          
          // Check if there's a current session
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData?.session) {
            console.log('Existing session detected, signing out user first');
            await supabase.auth.signOut();
            console.log('User signed out successfully');
          } else {
            console.log('No existing session detected, continuing with invite flow');
          }
          
          // Prepare redirect to password setting page with all relevant tokens and parameters
          const redirectParams = new URLSearchParams();
          
          // Preserve all query parameters from the callback URL
          searchParams.forEach((value, key) => {
            redirectParams.append(key, value);
          });
          
          // Add special flag to indicate this was from an invite link
          redirectParams.append('fromInvite', 'true');
          
          // Capture access token and other critical auth params from hash if present
          const accessToken = hashParams.get('access_token');
          if (accessToken) {
            redirectParams.append('access_token', accessToken);
          }
          
          const refreshToken = hashParams.get('refresh_token');
          if (refreshToken) {
            redirectParams.append('refresh_token', refreshToken);
          }
          
          // Add type=invite explicitly if it was present in hash
          if (typeInHash) {
            redirectParams.append('type', typeInHash);
          }
          
          // Preserve the entire hash as a param for maximum compatability
          if (window.location.hash) {
            const fullHash = window.location.hash.substring(1); // Remove the # character
            redirectParams.append('fullHash', fullHash);
          }
          
          // Redirect with all the parameters preserved
          console.log('Redirecting to password setting page with parameters:', 
            Object.fromEntries(redirectParams.entries()));
          
          setLoading(false);
          navigate(`/set-password?${redirectParams.toString()}`, { replace: true });
          return;
        }
        
        // For non-invite links, get session info and proceed normally
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Supabase session error:', error);
          setError('Er is een fout opgetreden bij het ophalen van uw sessie.');
          setLoading(false);
          return;
        }
        
        console.log('Session data:', data);
        setLoading(false);
        
        // Extract user and metadata from the session
        const userMetadata = data?.session?.user?.user_metadata || {};
        const typeInMetadata = userMetadata.type;
        const isAdminCreated = userMetadata.is_admin_created === true;
        
        // For non-invite URLs, check if the session itself indicates it's an invite
        const isInviteSession = typeInMetadata === 'invite' || isAdminCreated;
        
        console.log('Session-based invite detection:', { 
          typeInMetadata, 
          isAdminCreated,
          isInviteSession
        });
        
        // Navigate to the appropriate page
        if (isInviteSession) {
          console.log('Session indicates invite, redirecting to password setting page');
          
          // Create params with user information
          const redirectParams = new URLSearchParams();
          redirectParams.append('fromSession', 'true');
          
          if (data.session?.user?.email) {
            redirectParams.append('email', data.session.user.email);
          }
          
          navigate(`/set-password?${redirectParams.toString()}`, { replace: true });
        } else {
          console.log('Normal sign-in, redirecting to login page');
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error in auth callback:', error);
        setError('Er is een fout opgetreden bij het verwerken van de authenticatie.');
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        padding: 3,
        background: '#001350',
        backgroundImage: 'linear-gradient(135deg, #001350 0%, #002080 100%)'
      }}
    >
      {loading ? (
        <>
          <CircularProgress sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Verificatie wordt verwerkt...
          </Typography>
        </>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {error}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 500 }}>
          Verificatie geslaagd! U wordt doorgestuurd.
        </Alert>
      )}
    </Box>
  );
} 